import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector} from 'react-redux';
import CustomNotifications from 'shared/notification';
import NavContainer from '../navPanel/NavContainer';
import EditUserModal from './EditUserModal';
import { 
  Input, Loader
} from '@here/ref-client-ui';
import './style/admin.scss';
import {TableRow} from '../Table/TableRow';
import { fetchUsers,submitUserInfo}  from '../../utils/api/users/users';
import { set as setNotification } from "../../state/notification/actions";
import { getTabSettings } from '../../state/tabs/tab/preset/reducer';
import {getActiveUserInfo} from '../../state/selectedUser/index';
import {getSupportedCountries} from '../../state/countries/index';

const roleOptions = [
    { value: 'SUPERADMIN', label: 'SuperAdmin' },
    { value: 'ADMIN', label: 'Admin' },
    { value: 'USER', label: 'User' }
];

export default function Admin() {
    const notificationSystemRef = useRef();
    const dispatch = useDispatch();
    const [users, setUsers] = useState([]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [countryOptions, setCountryOptions] = useState([]);
    const [editingUser, setEditingUser] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const notifications = useSelector(state => state.notification);
    const settingsUrl = useSelector(state => getTabSettings(state)?.url);
    const settingsUserToken = useSelector(state => getTabSettings(state)?.token);
    const activeUserInfo = useSelector(state => getActiveUserInfo(state));
    const countries = useSelector(state => getSupportedCountries(state));


    const memoizedSettings = useMemo(() => {
        if(!settingsUrl || !settingsUserToken) return null;
        return {
            url: settingsUrl,
            token: settingsUserToken
        }
    },[settingsUrl,settingsUserToken]);

    const getCountryOptionsForUsers = (users) => {
        return users
        .flatMap(user => user.info.countries || [])
        .filter(country => country && country.code && country.name)
        .map(country => {
            return {
                value: country.code,
                label: country.name
            };
        });
    }

    const getCountryOptionsForUser = (user) => {
        user['countries'] = user.countries ? user.countries : [];
        if (typeof user === 'object' && user.countries) {
            return user.countries.map(country => ({
                value: country.code,
                label: country.name
            }));
        }
    }

    const { filteredUsers, userCountryList } = useMemo(() => {
        
        if (users.length === 0) {
            return { filteredUsers: [], userCountryList: [] };
        }
        
        if (!searchQuery.trim()) {
            return {
                filteredUsers: users,
                userCountryList: getCountryOptionsForUsers(users)
            };
        }
        const filteredUsers = users.filter(user => {
            const email = user.info.email || '';
            const role = user.info.roles[0] || '';
            return email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                   role.toLowerCase().includes(searchQuery.toLowerCase());
        });
        const userCountryList = getCountryOptionsForUsers(filteredUsers);
        return {
            filteredUsers,
            userCountryList
        };
    }, [users, searchQuery]);

    useEffect(() => {
        if (!memoizedSettings) return;
        const fetchData = async () => {
            setIsLoading(true);
            const users = await fetchUsers(memoizedSettings);
            (users && Array.isArray(users) && users.length > 0) ? setUsers(users) : setUsers([]);
            setIsLoading(false);
        };
        fetchData();
    }, [memoizedSettings]);

    useEffect(() => {
        if(notifications){
            notificationSystemRef.current.addNotification(notifications);
            dispatch(setNotification(null));
        }
    }, [notifications, dispatch]);

    const handleEditUser = (user) => {
        setIsEditModalOpen(true);
        setEditingUser({ ...user });
        const countryOptions = getCountryOptionsForUser(user);
        setCountryOptions(countryOptions);
    };

    const handleCloseModal = () => {
        setIsEditModalOpen(false);
        setEditingUser(null);
    };


    const handleSaveUser = async () => {

        setIsLoading(true);
        try {
            const editedUser = { 'info': {...editingUser, updatedBy: activeUserInfo.name}};
            let res = await submitUserInfo(editedUser,memoizedSettings);
            if( res.status === 200 || res.status === 201 ){

                const updatedUsers  = await fetchUsers(memoizedSettings);
                setUsers(updatedUsers);
                dispatch(setNotification({
                    message: `Users Updated Successfully.`,
                    impact: 'positive',
                    autoDismiss: 5,
                }));
                setIsEditModalOpen(false);
                setEditingUser(null);
            } else {
                setIsEditModalOpen(false);
                dispatch(setNotification({
                    message: res.message || `Users Not Updated.`,
                    impact: 'warning',
                    autoDismiss: 5,
                }));
            }
        } catch (error) {
            setIsEditModalOpen(false);
            dispatch(setNotification({
                message: `Users Not Updated.`,
                impact: 'warning',
                autoDismiss: 5,
                }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        if (field === 'countries') {
            const selectedCountries = countries.filter(country => value.includes(country.code));
            setEditingUser(
                prevUser => ({
                    ...prevUser,
                    [field]: [ ...selectedCountries]
                })
            );
        } else {
            setEditingUser(
                prevUser => ({
                    ...prevUser,
                    [field]: [value] 
                })
            );
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    return (
        <div className="rf-admin-main">
            <lui-default-theme dark>
                <CustomNotifications ref={notificationSystemRef} />
                <NavContainer showSearchBar={false} isDevMode={false} isUserContainer={true} />
                
                <div className="admin-panel">
                    <div className="admin-content">
                        <div className="admin-table-container">
                            <div className="admin-controls">
                                <div className="admin-search">
                                    <Input
                                        type="text"
                                        placeholder="Search users by email & role"
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        className="admin-search-input"
                                        style={{ height: '5rem' }}
                                    />
                                </div>
                            </div>
                            
                            <div className="admin-table-wrapper">
                                 
                                <table className="admin-users-table">
                                    <thead>
                                        <tr>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th className="admin-actions-header">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                       
                                        {filteredUsers && Array.isArray(filteredUsers) && filteredUsers.map((user) => (
                                            <TableRow key={user.userId} userInfo={user} handleEditUser={handleEditUser}  />
                                        ))}
                                    </tbody>
                                </table>
                                {filteredUsers && filteredUsers.length === 0 && (
                                    isLoading ? 
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px',backgroundColor: 'rgba(0,0,0,0.5)',zIndex: 9999 }}>
                                        <Loader />
                                        <span style={{ color: 'white', marginLeft: '10px' }}>Loading...</span>
                                    </div> 
                                    :
                                    <div className="admin-empty-state">
                                        <p>No users found matching your search criteria.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <EditUserModal
                        isOpen={isEditModalOpen}
                        isShown={isEditModalOpen}
                        onClose={handleCloseModal}
                        user={editingUser}
                        onUserChange={handleInputChange}
                        onSave={handleSaveUser}
                        isLoading={isLoading}
                        roleOptions={roleOptions}
                        countryOptions={editingUser ? countryOptions : userCountryList}
                        setCountryOptions={setCountryOptions}
                    />
                </div>
            </lui-default-theme>
        </div>
    );
}
